import { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import api, { SearchResults, formatMMK } from '@/lib/api'
import {
  LayoutDashboard, Users, UserPlus, Handshake, FileSpreadsheet,
  Database, Wrench, MapPin, Settings, LogOut, Search, X,
  ChevronDown, Download, User, Activity, ClipboardList, LayoutGrid, Columns4, Wallet, Receipt, Banknote, TrendingUp, ClipboardCheck, Network
} from 'lucide-react'
import NotificationPanel from '@/components/NotificationPanel'
import PushNotificationManager from '@/components/PushNotificationManager'
import BottomNav from '@/components/BottomNav'
import { usePwaInstall } from '@/hooks/usePwaInstall'
import { useQuery } from '@tanstack/react-query'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import type { PermissionKey } from '@/lib/api'

const ALL_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'chief_engineer', 'operation_manager', 'engineer', 'technician', 'finance', 'procurement', 'storekeeper', 'hr']
const PROJECT_BANK_ROLES = ALL_ROLES.filter(r => r !== 'hr' && r !== 'technician')
const CRM_ACCESS_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'finance']
const EXCEL_IMPORT_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive']

const BASE_NAV_ITEMS = [
  { section: 'CRM', items: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ALL_ROLES },
    { path: '/contacts', label: 'Leads & Contacts', icon: UserPlus, roles: CRM_ACCESS_ROLES, permKey: 'crm_view' as PermissionKey },
    { path: '/deals', label: 'Deals', icon: Handshake, roles: CRM_ACCESS_ROLES, permKey: 'crm_view' as PermissionKey },
    { path: '/import', label: 'Excel Importer', icon: FileSpreadsheet, roles: EXCEL_IMPORT_ROLES, permKey: 'project_bank_create' as PermissionKey },
  ]},
  { section: 'Operations', items: [
    { path: '/customers', label: 'Project Bank', icon: Database, roles: PROJECT_BANK_ROLES },
  ]},
  { section: 'Engineering', items: [
    { path: '/service-requests', label: 'Service Requests', icon: Wrench, roles: ALL_ROLES },
    { path: '/site-surveys', label: 'Site Surveys', icon: MapPin, roles: ALL_ROLES },
    { path: '/handover-forms', label: 'Handover Forms', icon: ClipboardCheck, roles: ALL_ROLES.filter(r => r !== 'hr'), permKey: 'handover_write' as PermissionKey },
  ]},
  { section: 'Finance', items: [
    { path: '/budget-requests', label: 'Budget Requests', icon: Wallet, roles: ALL_ROLES },
    { path: '/expense-claims', label: 'Expense Claims', icon: Receipt, roles: ALL_ROLES },
    { path: '/advance-cash', label: 'Advance Cash', icon: Banknote, roles: ALL_ROLES },
  ]},
  { section: 'Reports', items: [
    { path: '/analytics', label: 'Analytics', icon: TrendingUp, roles: ALL_ROLES },
    { path: '/crm-reports', label: 'CRM Reports', icon: Handshake, roles: CRM_ACCESS_ROLES, permKey: 'crm_view' as PermissionKey },
    { path: '/weekly-report', label: 'Weekly Report', icon: ClipboardList, roles: ALL_ROLES },
  ]},
  { section: 'Admin', items: [
    { path: '/users', label: 'User Management', icon: Settings, roles: ['super_admin', 'admin'] },
    { path: '/activity-logs', label: 'Activity Logs', icon: Activity, roles: ['super_admin', 'admin', 'director'], permKey: 'activity_logs_view' as PermissionKey },
    { path: '/org-chart', label: 'Org Chart', icon: Network, roles: ['super_admin', 'admin', 'director'] },
  ]},
]

export default function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { canInstall, promptInstall, isIos, isStandalone } = usePwaInstall()
  const { canDo, isSuccess: permsLoaded } = useMyPermissions()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null)
  const [showSearch, setShowSearch] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchTimeout = useRef<NodeJS.Timeout>()

  const { data: appSettings } = useQuery<{ project_management_enabled?: boolean }>({
    queryKey: ['app-settings'],
    queryFn: () => api.get('/project-management/settings').then(r => r.data),
    staleTime: 60000,
  })
  const pmEnabled = appSettings?.project_management_enabled === true

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null)
      setShowSearch(false)
      return
    }
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q: searchQuery } })
        setSearchResults(res.data)
        setShowSearch(true)
      } catch {}
    }, 300)
    return () => clearTimeout(searchTimeout.current)
  }, [searchQuery])

  const navItems = (() => {
    const result = [...BASE_NAV_ITEMS]
    if (pmEnabled) {
      const engIdx = result.findIndex(s => s.section === 'Engineering')
      const pmSection = {
        section: 'Project Management',
        items: [{ path: '/project-management', label: 'Project Management', icon: Columns4, roles: ALL_ROLES }],
      }
      result.splice(engIdx + 1, 0, pmSection)
    }
    return result
  })()

  const filteredNav = navItems.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (!user) return false
      if ((item as any).permKey) {
        if (!permsLoaded) return false
        return canDo((item as any).permKey)
      }
      return item.roles.includes(user.role)
    })
  })).filter(section => section.items.length > 0)

  const handleLogout = () => { logout(); navigate('/login') }

  const totalResults = searchResults
    ? searchResults.clients.length + searchResults.deals.length +
      searchResults.projects.length + searchResults.service_requests.length
    : 0

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden lg:flex w-64 bg-[#1a1a1a] text-white flex-col shrink-0">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <img src="/logo.png" alt="Logo" className="h-8 w-8 rounded" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
          <span className="font-bold text-lg tracking-tight">Project One</span>
        </div>
        <nav data-nav className="flex-1 overflow-y-auto py-3 px-3">
          {filteredNav.map(section => (
            <div key={section.section} className="mb-4">
              <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{section.section}</div>
              {section.items.map(item => {
                const active = location.pathname === item.path
                return (
                  <button key={item.path} onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors mb-0.5 ${active ? 'bg-red-800/20 text-red-400' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}>
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => navigate('/profile')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors mb-1 ${location.pathname === '/profile' ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
          >
            <User size={18} />
            <span>My Profile</span>
          </button>
          {!isStandalone && (canInstall || isIos) && (
            <button
              onClick={() => {
                if (canInstall) { promptInstall() }
                else if (isIos) { alert('To install: tap the Share button in Safari, then tap "Add to Home Screen".') }
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors mb-1"
            >
              <Download size={18} />
              <span>Install App</span>
            </button>
          )}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-red-800 flex items-center justify-center text-sm font-semibold">
              {user?.full_name?.charAt(0) || user?.username?.charAt(0) || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name || user?.username}</div>
              <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
            </div>
            <button onClick={handleLogout} className="text-slate-400 hover:text-white p-1" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-12 sm:h-14 bg-white border-b border-slate-200 flex items-center px-3 sm:px-4 gap-2 sm:gap-4 shrink-0">
          <div className="relative flex-1 max-w-lg" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
            />
            {showSearch && searchResults && totalResults > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-80 overflow-y-auto z-50">
                {searchResults.clients.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Clients ({searchResults.clients.length})</div>
                    {searchResults.clients.map(c => (
                      <button key={c.id} className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2 min-h-[44px]"
                        onClick={() => { navigate('/contacts'); setShowSearch(false); setSearchQuery('') }}>
                        <Users size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate">{c.client_name}</span>
                        <span className="text-xs text-slate-400 ml-auto shrink-0">{c.contact_type}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.deals.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Deals ({searchResults.deals.length})</div>
                    {searchResults.deals.map(d => (
                      <button key={d.id} className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2 min-h-[44px]"
                        onClick={() => { navigate('/deals'); setShowSearch(false); setSearchQuery('') }}>
                        <Handshake size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate">{d.deal_name}</span>
                        <span className="text-xs text-slate-400 ml-auto shrink-0">{formatMMK(Number(d.value))}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.projects.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Projects ({searchResults.projects.length})</div>
                    {searchResults.projects.map(p => (
                      <button key={p.id} className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2 min-h-[44px]"
                        onClick={() => { navigate('/customers'); setShowSearch(false); setSearchQuery('') }}>
                        <Database size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate">{p.project_type}-{p.project_code}</span>
                        <span className="text-xs text-slate-400 ml-auto shrink-0">{p.client_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.service_requests.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Service Requests ({searchResults.service_requests.length})</div>
                    {searchResults.service_requests.map(sr => (
                      <button key={sr.id} className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2 min-h-[44px]"
                        onClick={() => { navigate('/service-requests'); setShowSearch(false); setSearchQuery('') }}>
                        <Wrench size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate">{sr.sr_number}</span>
                        <span className="text-xs text-slate-400 ml-auto shrink-0">{sr.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {showSearch && searchResults && totalResults === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-4 text-center text-sm text-slate-500 z-50">
                No results found
              </div>
            )}
          </div>
          <PushNotificationManager />
          <NotificationPanel />
          <button onClick={handleLogout} className="lg:hidden p-2 text-slate-400 hover:text-slate-600" title="Logout">
            <LogOut size={20} />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 bg-gray-100 pb-20 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {user && <BottomNav userRole={user.role} pmEnabled={pmEnabled} />}
    </div>
  )
}
